/**
 * Character Import Modal
 *
 * Import characters from various sources:
 * - JSON file (Machina format or D&D Beyond export)
 * - PDF character sheet (AI-powered parsing)
 * - Image of character sheet (AI-powered parsing)
 */

import { useState, useCallback, useRef } from 'react';
import { Icon } from './Icon';
import { useCharacterStore } from '../stores/characterStore';
import { trpc } from '../lib/trpc';
import type { AbilityScores } from '@machina/core';

interface CharacterImportModalProps {
  onClose: () => void;
  onImported?: (characterId: string) => void;
}

const GOLD = '#d4af37';
const GOLD_DIM = 'rgba(212, 175, 55, 0.3)';

type ImportTab = 'json' | 'pdf' | 'image';

// Machina character import format (simplified)
interface ImportedCharacter {
  name: string;
  playerName?: string;
  race: string;
  classes: { name: string; level: number; subclass?: string }[];
  abilityScores: AbilityScores;
  armorClass: number;
  maxHp: number;
  currentHp?: number;
  speed?: number;
  proficiencyBonus?: number;
  // Optional extras
  background?: string;
  alignment?: string;
  features?: string[];
  equipment?: string[];
  spells?: string[];
}

export function CharacterImportModal({ onClose, onImported }: CharacterImportModalProps) {
  const [activeTab, setActiveTab] = useState<ImportTab>('json');
  const [jsonInput, setJsonInput] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parsedCharacter, setParsedCharacter] = useState<ImportedCharacter | null>(null);
  const [importStep, setImportStep] = useState<'input' | 'preview'>('input');

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Store access
  const addCharacter = useCharacterStore((s) => s.addCharacter);
  const addToParty = useCharacterStore((s) => s.addToParty);


  // Parse JSON input
  const parseJson = useCallback(() => {
    setError(null);
    try {
      const data = JSON.parse(jsonInput);

      // Try to detect format and normalize
      const character = normalizeCharacterData(data);
      if (!character) {
        throw new Error('Could not parse character data. Unrecognized format.');
      }

      setParsedCharacter(character);
      setImportStep('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid JSON');
    }
  }, [jsonInput]);

  // Handle file upload
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError(null);

    // If JSON file, read and parse it
    if (selectedFile.type === 'application/json' || selectedFile.name.endsWith('.json')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        setJsonInput(content);
        setActiveTab('json');
      };
      reader.readAsText(selectedFile);
    }
  }, []);

  // Process PDF/Image with AI
  const processWithAI = useCallback(async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Convert file to base64
      const base64 = await fileToBase64(file);
      const mimeType = file.type || 'application/octet-stream';

      // Send to AI for parsing
      const result = await trpc.characters.parseCharacterSheet.mutate({
        fileData: base64,
        mimeType,
        fileName: file.name,
      });

      if (result.success && 'character' in result) {
        setParsedCharacter(result.character as ImportedCharacter);
        setImportStep('preview');
      } else if ('error' in result) {
        setError(result.error || 'Failed to parse character sheet');
      } else {
        setError('Failed to parse character sheet');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process file');
    } finally {
      setIsProcessing(false);
    }
  }, [file]);

  // Import the parsed character
  const handleImport = useCallback(async () => {
    if (!parsedCharacter) return;

    setIsProcessing(true);
    setError(null);

    try {
      // Calculate derived values
      const totalLevel = parsedCharacter.classes.reduce((sum, c) => sum + c.level, 0);
      const profBonus = parsedCharacter.proficiencyBonus || Math.ceil(1 + totalLevel / 4);
      const primaryClass = parsedCharacter.classes[0];
      const hitDie = getHitDieForClass(primaryClass?.name || 'Fighter');

      // Create the character
      const character = addCharacter({
        name: parsedCharacter.name,
        playerName: parsedCharacter.playerName,
        race: parsedCharacter.race,
        classes: parsedCharacter.classes.map(c => ({
          name: c.name,
          level: c.level,
          subclass: c.subclass,
          hitDie: getHitDieForClass(c.name),
        })),
        abilityScores: parsedCharacter.abilityScores,
        proficiencyBonus: profBonus,
        armorClass: parsedCharacter.armorClass,
        maxHp: parsedCharacter.maxHp,
        currentHp: parsedCharacter.currentHp ?? parsedCharacter.maxHp,
        hitDice: `${totalLevel}d${hitDie}`,
        hitDiceRemaining: totalLevel,
        speed: parsedCharacter.speed || 30,
      });

      // Add to party
      addToParty(character.id);

      onImported?.(character.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import character');
    } finally {
      setIsProcessing(false);
    }
  }, [parsedCharacter, addCharacter, addToParty, onImported, onClose]);

  // Render tab content
  const renderTabContent = () => {
    if (importStep === 'preview' && parsedCharacter) {
      return renderPreview();
    }

    switch (activeTab) {
      case 'json':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>
              Paste character JSON (Machina format or D&D Beyond export)
            </div>
            <textarea
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              placeholder='{"name": "Aragorn", "race": "Human", ...}'
              style={{
                width: '100%',
                height: 200,
                padding: 12,
                background: 'rgba(30, 25, 40, 0.8)',
                border: '1px solid rgba(212, 175, 55, 0.3)',
                borderRadius: 4,
                color: '#fff',
                fontSize: 12,
                fontFamily: 'monospace',
                resize: 'vertical',
              }}
            />
            <button
              type="button"
              onClick={parseJson}
              disabled={!jsonInput.trim()}
              style={{
                padding: '10px 20px',
                background: jsonInput.trim() ? GOLD : 'rgba(100, 80, 60, 0.5)',
                border: 'none',
                borderRadius: 4,
                color: '#000',
                fontSize: 13,
                fontWeight: 600,
                cursor: jsonInput.trim() ? 'pointer' : 'not-allowed',
              }}
            >
              Parse JSON
            </button>
          </div>
        );

      case 'pdf':
      case 'image':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>
              Upload a {activeTab === 'pdf' ? 'PDF' : 'image'} of a character sheet.
              AI will extract the character data.
            </div>

            {/* Drop zone */}
            <div
              onClick={() => fileInputRef.current?.click()}
              style={{
                padding: 40,
                border: `2px dashed ${file ? GOLD : 'rgba(255,255,255,0.2)'}`,
                borderRadius: 8,
                textAlign: 'center',
                cursor: 'pointer',
                background: file ? 'rgba(212, 175, 55, 0.05)' : 'transparent',
                transition: 'all 0.2s',
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={activeTab === 'pdf' ? '.pdf' : 'image/*'}
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <Icon
                name={file ? 'check' : 'plus'}
                size={32}
                color={file ? GOLD : 'rgba(255,255,255,0.3)'}
              />
              <div style={{
                marginTop: 12,
                fontSize: 13,
                color: file ? GOLD : 'rgba(255,255,255,0.5)'
              }}>
                {file ? file.name : `Click to select ${activeTab === 'pdf' ? 'PDF' : 'image'}`}
              </div>
              {!file && (
                <div style={{ marginTop: 4, fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>
                  Supports Roll20, D&D Beyond, and standard 5e character sheets
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={processWithAI}
              disabled={!file || isProcessing}
              style={{
                padding: '10px 20px',
                background: file && !isProcessing ? GOLD : 'rgba(100, 80, 60, 0.5)',
                border: 'none',
                borderRadius: 4,
                color: '#000',
                fontSize: 13,
                fontWeight: 600,
                cursor: file && !isProcessing ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              {isProcessing ? (
                <>
                  <Icon name="sparkle" size={14} />
                  Analyzing with AI...
                </>
              ) : (
                <>
                  <Icon name="sparkle" size={14} />
                  Extract Character Data
                </>
              )}
            </button>
          </div>
        );
    }
  };

  // Render character preview
  const renderPreview = () => {
    if (!parsedCharacter) return null;

    const totalLevel = parsedCharacter.classes.reduce((sum, c) => sum + c.level, 0);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{
          padding: 16,
          background: 'rgba(20, 15, 30, 0.8)',
          borderRadius: 4,
          border: `1px solid ${GOLD_DIM}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Icon name="check" size={16} color="#4ade80" />
            <span style={{ fontSize: 12, color: '#4ade80' }}>Character parsed successfully</span>
          </div>

          <h3 style={{ fontSize: 18, margin: 0, color: GOLD }}>{parsedCharacter.name}</h3>
          {parsedCharacter.playerName && (
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>
              Played by {parsedCharacter.playerName}
            </div>
          )}
          <div style={{ fontSize: 14, marginTop: 8 }}>
            {parsedCharacter.race}{' '}
            {parsedCharacter.classes.map((c, i) => (
              <span key={i}>
                {i > 0 && ' / '}
                {c.name} {c.level}
                {c.subclass && <span style={{ color: 'rgba(255,255,255,0.5)' }}> ({c.subclass})</span>}
              </span>
            ))}
          </div>
        </div>

        {/* Stats Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          <div style={{ padding: 10, background: 'rgba(30, 25, 40, 0.6)', borderRadius: 4, textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: GOLD }}>HP</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: '#ef4444' }}>{parsedCharacter.maxHp}</div>
          </div>
          <div style={{ padding: 10, background: 'rgba(30, 25, 40, 0.6)', borderRadius: 4, textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: GOLD }}>AC</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: '#60a5fa' }}>{parsedCharacter.armorClass}</div>
          </div>
          <div style={{ padding: 10, background: 'rgba(30, 25, 40, 0.6)', borderRadius: 4, textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: GOLD }}>Level</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: '#a78bfa' }}>{totalLevel}</div>
          </div>
        </div>

        {/* Abilities */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 4 }}>
          {(['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'] as const).map(ability => {
            const score = parsedCharacter.abilityScores[ability];
            const mod = Math.floor((score - 10) / 2);
            return (
              <div key={ability} style={{
                padding: 6,
                background: 'rgba(30, 25, 40, 0.6)',
                borderRadius: 4,
                textAlign: 'center',
              }}>
                <div style={{ fontSize: 9, color: GOLD }}>{ability.slice(0, 3).toUpperCase()}</div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{score}</div>
                <div style={{ fontSize: 10, color: mod >= 0 ? '#86efac' : '#f87171' }}>
                  {mod >= 0 ? '+' : ''}{mod}
                </div>
              </div>
            );
          })}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button
            type="button"
            onClick={() => {
              setImportStep('input');
              setParsedCharacter(null);
            }}
            style={{
              flex: 1,
              padding: '10px 16px',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 4,
              color: 'rgba(255,255,255,0.7)',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={isProcessing}
            style={{
              flex: 2,
              padding: '10px 20px',
              background: '#22c55e',
              border: 'none',
              borderRadius: 4,
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            <Icon name="plus" size={14} />
            Import Character
          </button>
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 480,
          maxHeight: '85vh',
          background: 'linear-gradient(180deg, #1a1520 0%, #0f0d12 100%)',
          border: `1px solid ${GOLD_DIM}`,
          borderRadius: 8,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${GOLD_DIM}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div>
            <h2 style={{ fontSize: 16, margin: 0, color: GOLD }}>Import Character</h2>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
              Import from JSON, PDF, or image
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'rgba(255,255,255,0.5)',
            }}
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        {/* Tabs */}
        {importStep === 'input' && (
          <div style={{
            display: 'flex',
            borderBottom: `1px solid ${GOLD_DIM}`,
          }}>
            {[
              { id: 'json' as const, label: 'JSON', icon: 'scroll' as const },
              { id: 'pdf' as const, label: 'PDF', icon: 'scroll' as const },
              { id: 'image' as const, label: 'Image', icon: 'eye' as const },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  background: activeTab === tab.id ? 'rgba(212, 175, 55, 0.1)' : 'transparent',
                  border: 'none',
                  borderBottom: activeTab === tab.id ? `2px solid ${GOLD}` : '2px solid transparent',
                  color: activeTab === tab.id ? GOLD : 'rgba(255,255,255,0.5)',
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <Icon name={tab.icon} size={14} />
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div style={{
          flex: 1,
          padding: 20,
          overflowY: 'auto',
        }}>
          {renderTabContent()}
        </div>

        {/* Error */}
        {error && (
          <div style={{
            margin: '0 20px 12px',
            padding: 10,
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: 4,
            fontSize: 12,
            color: '#ef4444',
          }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function getHitDieForClass(className: string): number {
  const hitDice: Record<string, number> = {
    barbarian: 12,
    fighter: 10,
    paladin: 10,
    ranger: 10,
    bard: 8,
    cleric: 8,
    druid: 8,
    monk: 8,
    rogue: 8,
    warlock: 8,
    sorcerer: 6,
    wizard: 6,
    artificer: 8,
  };
  return hitDice[className.toLowerCase()] || 8;
}

// Normalize character data from different formats
function normalizeCharacterData(data: any): ImportedCharacter | null {
  // Try Machina format first
  if (data.name && data.abilityScores && data.classes) {
    return {
      name: data.name,
      playerName: data.playerName,
      race: data.race || 'Unknown',
      classes: Array.isArray(data.classes) ? data.classes : [{ name: data.classes, level: 1 }],
      abilityScores: data.abilityScores,
      armorClass: data.armorClass || data.ac || 10,
      maxHp: data.maxHp || data.hp || 10,
      currentHp: data.currentHp,
      speed: data.speed,
      proficiencyBonus: data.proficiencyBonus,
    };
  }

  // Try D&D Beyond format
  if (data.data?.name || data.name) {
    const ddb = data.data || data;

    // D&D Beyond uses base + modifier for stats
    const stats = ddb.stats || ddb.abilities || [];
    const getStatValue = (index: number) => {
      const stat = stats[index];
      if (typeof stat === 'number') return stat;
      if (stat?.value) return stat.value;
      return 10;
    };

    // Extract classes
    const classes = (ddb.classes || []).map((c: any) => ({
      name: c.definition?.name || c.name || 'Unknown',
      level: c.level || 1,
      subclass: c.subclassDefinition?.name || c.subclass,
    }));

    if (classes.length === 0) {
      classes.push({ name: 'Fighter', level: 1 });
    }

    return {
      name: ddb.name || 'Unknown',
      playerName: ddb.playerName,
      race: ddb.race?.fullName || ddb.race?.baseName || ddb.race || 'Unknown',
      classes,
      abilityScores: {
        strength: getStatValue(0),
        dexterity: getStatValue(1),
        constitution: getStatValue(2),
        intelligence: getStatValue(3),
        wisdom: getStatValue(4),
        charisma: getStatValue(5),
      },
      armorClass: ddb.armorClass || ddb.ac || 10,
      maxHp: ddb.baseHitPoints || ddb.maxHp || ddb.hp || 10,
      currentHp: ddb.currentHp,
      speed: ddb.speed?.walking || ddb.speed || 30,
    };
  }

  // Try simple format { name, str, dex, con, int, wis, cha, ... }
  if (data.name && (data.str || data.strength)) {
    return {
      name: data.name,
      playerName: data.playerName || data.player,
      race: data.race || 'Unknown',
      classes: [{
        name: data.class || data.className || 'Fighter',
        level: data.level || 1,
        subclass: data.subclass,
      }],
      abilityScores: {
        strength: data.str || data.strength || 10,
        dexterity: data.dex || data.dexterity || 10,
        constitution: data.con || data.constitution || 10,
        intelligence: data.int || data.intelligence || 10,
        wisdom: data.wis || data.wisdom || 10,
        charisma: data.cha || data.charisma || 10,
      },
      armorClass: data.ac || data.armorClass || 10,
      maxHp: data.hp || data.maxHp || 10,
      currentHp: data.currentHp,
      speed: data.speed || 30,
    };
  }

  return null;
}
